import { z } from "zod";
import { api, tokenStore } from "./client";

/* ──────────────────────────────────────────────────────────────────────
   Mirrors api/routers/agent.py. SuperAdmin only.
   ────────────────────────────────────────────────────────────────────── */

export const AgentTool = z.object({
  name:        z.string(),
  risk:        z.string(),
  description: z.string(),
  parameters:  z.record(z.unknown()),
  enabled:     z.boolean(),
});
export type AgentTool = z.infer<typeof AgentTool>;

export const AgentStatus = z.object({
  settings: z.object({
    provider:        z.string(),
    api_base:        z.string().nullable(),
    model:           z.string().nullable(),
    configured:      z.boolean(),
    has_api_key:     z.boolean(),
    max_steps:       z.number().int(),
    repo_root:       z.string(),
    exec_mode:       z.string(),
    np92_alias:      z.string(),
    np92_project_dir: z.string(),
    approval_policy: z.string(),
    allow_edits:     z.boolean(),
    allow_sql_writes: z.boolean(),
    commit_mode:     z.string(),
    sync_cmd_set:    z.boolean(),
    changelog:       z.string(),
    backup_dir:      z.string(),
    sessions_dir:    z.string(),
    hidden_path_count: z.number().int(),
  }),
  tools:             z.array(AgentTool),
  defaults:          z.object({ approval_mode: z.string(), reasoning_effort: z.string().nullable(), compact_at_chars: z.number() }).optional(),
  active_sessions:   z.array(z.string()),
  pending_approvals: z.array(z.object({ session_id: z.string(), call_id: z.string() })),
});
export type AgentStatus = z.infer<typeof AgentStatus>;

export const SessionSummary = z.object({
  id:           z.string(),
  title:        z.string(),
  created_by:   z.string(),
  created_at:   z.string(),
  updated_at:   z.string(),
  status:       z.string(),
  run_status:   z.string().optional(),
  harness_version: z.string().optional(),
  pending_call_id: z.string().nullable().optional(),
  approval_mode: z.string().nullable().optional(),
  reasoning_effort: z.string().nullable().optional(),
  history_chars: z.number().optional(),
  compactions:  z.number().optional(),
  turns:        z.number().int(),
  tool_calls:   z.number().int(),
  last_preview: z.string(),
  model:        z.string().nullable(),
});
export type SessionSummary = z.infer<typeof SessionSummary>;

/** One UI-facing transcript entry (also the shape of streamed events). */
export interface TranscriptItem {
  type: "user" | "assistant" | "reasoning" | "system" | "tool_call" | "tool_result" | "approval_required" | "approval_result" | "error"
      | "question" | "steer" | "ledger" | "change_record" | "risk_escalated" | "report_check";
  /** per-response throughput — the speed of THIS reply, not a session total */
  tok_s?: number | null;
  out_tokens?: number;
  in_tokens?: number;
  round_ms?: number;
  question?: string;
  context?: string;
  options?: string[];
  items?: Array<{ id: string; text: string; status: string; evidence?: string }>;
  always_asks?: boolean;
  chars?: number;
  text?: string;
  call_id?: string;
  name?: string;
  arguments?: Record<string, unknown>;
  ok?: boolean;
  result?: string;
  duration_ms?: number;
  approved?: boolean;
  risk?: string;
  /** set when the harness judged this CALL riskier than the tool's declared class */
  declared_risk?: string;
  why?: string[];
  /** change_record: the harness's own record of what the turn changed */
  data?: Record<string, unknown>;
  by?: string;
  flagged?: boolean;
  reason?: string;
  at?: string;
  step?: number;
}

export const SessionDetail = z.object({
  id:          z.string(),
  title:       z.string(),
  created_by:  z.string(),
  created_at:  z.string(),
  updated_at:  z.string(),
  status:      z.string(),
  transcript:  z.array(z.record(z.unknown())),
  usage:       z.record(z.unknown()),
  model:       z.string().nullable(),
  pending_approvals: z.array(z.string()),
  running:     z.boolean(),
  run_status:  z.string().optional(),                       // idle | running | awaiting_approval
  pending_call: z.record(z.unknown()).nullable().optional(),  // {call_id,name,arguments,risk} while parked
  approval_mode: z.string().nullable().optional(),           // session override (null = server default)
  reasoning_effort: z.string().nullable().optional(),
  effective_approval_mode: z.string().optional(),            // what the next turn will actually use
  effective_reasoning_effort: z.string().nullable().optional(),
  history_chars: z.number().optional(),                      // size of the model-facing history
  compact_at_chars: z.number().optional(),                   // auto-compact threshold (0 = off)
  compactions: z.number().optional(),
  harness_version: z.string().optional(),                    // v1 | v2
  pending_question: z.record(z.unknown()).nullable().optional(),
  queued_steers: z.array(z.string()).optional(),
  turn_id: z.string().nullable().optional(),
  ledger: z.array(z.record(z.unknown())).optional(),
});
export type ApprovalMode = "bypass" | "ask";
export type ReasoningEffort = "xhigh" | "medium" | "low";
export interface SessionSettings { approval_mode?: ApprovalMode; reasoning_effort?: ReasoningEffort }
export type SessionDetail = z.infer<typeof SessionDetail>;

export async function fetchAgentStatus(harness: string = "v1"): Promise<AgentStatus> {
  const r = await api.get("/api/agent/status", { params: { harness } });
  return AgentStatus.parse(r.data);
}

export async function fetchAgentBrief(harness: string = "v1"): Promise<string> {
  const r = await api.get("/api/agent/brief", { params: { harness } });
  return String((r.data as { system_prompt?: string }).system_prompt ?? "");
}

export async function listSessions(): Promise<SessionSummary[]> {
  const r = await api.get("/api/agent/sessions");
  return z.array(SessionSummary).parse((r.data as { sessions: unknown }).sessions);
}

export async function createSession(title?: string, harness: string = "v1"): Promise<SessionDetail> {
  const r = await api.post("/api/agent/sessions", { title: title ?? null, harness });
  return SessionDetail.parse({ ...(r.data as object), pending_approvals: [], running: false });
}

/** Send a message INTO the running turn (v2). It is injected after the current tool
 *  results and before the next model call — the running tool is never cancelled. */
export async function steerRun(id: string, content: string, expectedTurn?: string | null): Promise<{ queued: number; turn_id: string }> {
  const r = await api.post(`/api/agent/sessions/${encodeURIComponent(id)}/steer`, { content, expected_turn: expectedTurn ?? null });
  return r.data as { queued: number; turn_id: string };
}

export async function clearSteers(id: string): Promise<{ cleared: number }> {
  const r = await api.delete(`/api/agent/sessions/${encodeURIComponent(id)}/steer`);
  return r.data as { cleared: number };
}

export async function getSession(id: string): Promise<SessionDetail> {
  const r = await api.get(`/api/agent/sessions/${encodeURIComponent(id)}`);
  return SessionDetail.parse(r.data);
}

export async function deleteSession(id: string): Promise<void> {
  await api.delete(`/api/agent/sessions/${encodeURIComponent(id)}`);
}

export async function patchSession(id: string, settings: SessionSettings): Promise<void> {
  await api.patch(`/api/agent/sessions/${encodeURIComponent(id)}`, settings);
}

export async function cancelRun(id: string): Promise<{ cancelled: boolean; run_status: string }> {
  const r = await api.post(`/api/agent/sessions/${encodeURIComponent(id)}/cancel`, {});
  return r.data as { cancelled: boolean; run_status: string };
}

export interface ToolRunResult { ok: boolean; result: string; duration_ms: number; risk?: string }

export async function runTool(name: string, args: Record<string, unknown>): Promise<ToolRunResult> {
  const r = await api.post("/api/agent/tools/run", { name, arguments: args }, { timeout: 660_000 });
  return r.data as ToolRunResult;
}

export async function fetchChanges(): Promise<{ changelog: string; backups: Array<{ stamp: string; files: string[] }> }> {
  const r = await api.get("/api/agent/changes");
  return r.data as { changelog: string; backups: Array<{ stamp: string; files: string[] }> };
}

/* ── SSE streams ────────────────────────────────────────────────────────
   The run lives on the server (T17). Each of these only WATCHES it: close the
   tab and the run continues; call streamEvents to re-attach. Uses fetch (axios
   can't stream in browsers). On a 401 we ping /api/auth/me so the axios refresh
   interceptor renews the token, then retry once. Keepalive comment lines
   (": keepalive") are ignored by the parser. */
async function streamSSE(
  url: string,
  init: RequestInit,
  onEvent: (ev: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const attempt = async (retry: boolean): Promise<void> => {
    const res = await fetch(url, {
      ...init,
      headers: { ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${tokenStore.access ?? ""}` },
      signal,
    });
    if (res.status === 401 && retry) {
      await api.get("/api/auth/me");
      return attempt(false);
    }
    if (!res.ok || !res.body) {
      let detail = `HTTP ${res.status}`;
      try { const j = await res.json() as { detail?: string }; if (j.detail) detail = j.detail; } catch { /* ignore */ }
      throw new Error(detail);
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data:")) continue;
          try { onEvent(JSON.parse(line.slice(5).trim()) as Record<string, unknown>); } catch { /* skip */ }
        }
      }
    }
  };
  await attempt(true);
}

/** Start a turn from a new user message and watch it. Settings ride along and are persisted on the session. */
export function streamMessage(sessionId: string, content: string, onEvent: (ev: Record<string, unknown>) => void, signal?: AbortSignal, settings?: SessionSettings): Promise<void> {
  return streamSSE(`/api/agent/sessions/${encodeURIComponent(sessionId)}/messages`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content, ...(settings ?? {}) }) }, onEvent, signal);
}

/** /compact — summarise the model-facing history into a handoff note; streams the note back. */
export function streamCompact(sessionId: string, onEvent: (ev: Record<string, unknown>) => void, signal?: AbortSignal): Promise<void> {
  return streamSSE(`/api/agent/sessions/${encodeURIComponent(sessionId)}/compact`, { method: "POST" }, onEvent, signal);
}

/** Record an approve/deny for the parked call and watch the resumed run. */
export function streamApprove(sessionId: string, callId: string, approve: boolean, onEvent: (ev: Record<string, unknown>) => void, signal?: AbortSignal): Promise<void> {
  return streamSSE(`/api/agent/sessions/${encodeURIComponent(sessionId)}/approve`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ call_id: callId, approve }) }, onEvent, signal);
}

/** Re-attach to a run already in progress (reconnect / another tab). Emits one
 *  terminal turn_done immediately if nothing is live. */
export function streamEvents(sessionId: string, onEvent: (ev: Record<string, unknown>) => void, signal?: AbortSignal): Promise<void> {
  return streamSSE(`/api/agent/sessions/${encodeURIComponent(sessionId)}/events`, { method: "GET" }, onEvent, signal);
}
